import React from 'react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import type { LucideIcon } from 'lucide-react';

export interface MetricCardProps {
  title: string;
  value: string;
  changeText: string;
  changeColor?: string;
  icon: LucideIcon;
  iconBgColor: string;
  iconColor: string;
  sparklineData: Array<{ value: number }>;
  sparklineColor: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  changeText,
  changeColor = 'text-emerald-600',
  icon: Icon,
  iconBgColor,
  iconColor,
  sparklineData,
  sparklineColor,
}) => {
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#ECECF2] shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
      {/* Left Details */}
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl ${iconBgColor} ${iconColor} flex items-center justify-center shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-slate-400">{title}</span>
          <span className="text-2xl font-extrabold text-slate-800 tracking-tight mt-0.5">{value}</span>
          <span className={`text-[11px] font-bold mt-1 ${changeColor}`}>
            {changeText}
          </span>
        </div>
      </div>

      {/* Right Sparkline Chart */}
      <div className="w-24 h-12 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparklineData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={`gradient-${title.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={sparklineColor} stopOpacity={0.4} />
                <stop offset="95%" stopColor={sparklineColor} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={sparklineColor}
              strokeWidth={2.5}
              fillOpacity={1}
              fill={`url(#gradient-${title.replace(/\s+/g, '')})`}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
